/**
 * 図鑑タブの純粋な計算（距離・並び・頁・はらい・状態の札）。画面に触れないので単体テストできる。
 * 確定値の出どころ: README 図鑑タブ ㉖〜㉙。
 */
import type { ActiveEffect, GameState, StateArea, StateSpot } from '../../api/types';

export const PAGE_SIZE = 6;
export const SWIPE_MIN_PX = 40;
export const SWIPE_RATIO = 1.5;

export interface LatLon {
  lat: number;
  lon: number;
}

/** 2点間の直線距離（m）。表示用のみ（得点・判定には使わない） */
export function distanceM(a: LatLon, b: LatLon): number {
  const R = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export interface OrderedSpot<T> {
  spot: T;
  /** 現在地からの距離（m）。位置が無ければ null */
  dist: number | null;
}

/** 目次の並び。位置があれば近い順（同じ距離はサーバーの順）、無ければサーバーの順のまま */
export function orderByDistance<T extends { latitude: number; longitude: number }>(spots: readonly T[], from: LatLon | null): OrderedSpot<T>[] {
  const rows = spots.map((spot, i) => ({ spot, i, dist: from ? distanceM(from, { lat: spot.latitude, lon: spot.longitude }) : null }));
  if (from) rows.sort((a, b) => (a.dist as number) - (b.dist as number) || a.i - b.i);
  return rows.map(({ spot, dist }) => ({ spot, dist }));
}

/** 6件ずつの頁に分ける。空でも1頁（空の頁）を返す */
export function paginate<T>(items: readonly T[], size = PAGE_SIZE): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
  return pages.length ? pages : [[]];
}

/** 並びの何番目が何頁の何行目か（0始まり） */
export function positionOf(orderIndex: number, size = PAGE_SIZE): { page: number; row: number } {
  return { page: Math.floor(orderIndex / size), row: orderIndex % size };
}

/** 左右にはらってめくる条件（㉗）：横40px以上かつ縦の1.5倍以上。左へはらうと次の頁 */
export function swipeDirection(dx: number, dy: number): 'next' | 'prev' | null {
  const ax = Math.abs(dx);
  if (ax < SWIPE_MIN_PX || ax < Math.abs(dy) * SWIPE_RATIO) return null;
  return dx < 0 ? 'next' : 'prev';
}

export interface DistanceText {
  /** 「約」を頭に付けるか（GPS が ok でないとき・㉙） */
  approx: boolean;
  /** 数字だけ（1,200 の形） */
  num: string;
  unit: 'm';
}

export function fmtDistance(m: number, gpsStatus: string): DistanceText {
  return { approx: gpsStatus !== 'ok', num: Math.round(m).toLocaleString('en-US'), unit: 'm' };
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export function roman(n: number): string {
  return ROMAN[n - 1] ?? String(n);
}

/** 本の画像（book_w1〜w5）。章の番号に合わせ、5を超えたら5 */
export function bookImageIndex(areaIndex: number): number {
  return Math.min(Math.max(areaIndex, 0), 4) + 1;
}

/** エリアの色。サーバーが色名なら tokens の変数、#16進/rgb ならそのまま。それ以外は無難な茶 */
const AREA_ORDER_COLORS = ['var(--area-red)', 'var(--area-amber)', 'var(--area-blue)', 'var(--area-green)'];

/** 色が空ならエリアの並び順で切手の4色を使う（地図の⑮と同じ割り当て） */
export function areaColorCss(color: string | null, index = 0): string {
  if (!color || !color.trim()) return AREA_ORDER_COLORS[index % AREA_ORDER_COLORS.length]!;
  const c = color.trim();
  if (/^#[0-9a-f]{3,8}$/i.test(c) || /^rgba?\([\d\s.,%]+\)$/i.test(c)) return c;
  if (/^[a-z]+$/i.test(c)) return `var(--area-${c.toLowerCase()}, #6b5640)`;
  return '#6b5640';
}

// ---------------------------------------------------------------- 状態の札（㉙）

export interface AreaStatus {
  /** スズメバチの襲来（自チームが出せない） */
  hornet: { sourceName: string | null; endsAt: string } | null;
  /** 蜜源（エリアの倍率効果） */
  honey: { multiplier: number; endsAt: string | null } | null;
  /** 縄張り争い（自チームが対戦中。エリアに関係なく全エリアに出る） */
  duel: { mine: number; theirs: number; opponentName: string; endsAt: string | null } | null;
}

function live(e: ActiveEffect, nowMs: number): boolean {
  return Date.parse(e.starts_at) <= nowMs && (e.ends_at === null || Date.parse(e.ends_at) > nowMs);
}

export function deriveStatus(state: GameState, areaId: string, nowMs: number): AreaStatus {
  const me = state.team.team_id;
  const area: StateArea | undefined = state.areas.find((a) => a.area_id === areaId);
  const effects = state.active_effects.filter((e) => live(e, nowMs));

  let hornet: AreaStatus['hornet'] = null;
  if (area?.blocked_until && Date.parse(area.blocked_until) > nowMs) {
    const src = effects.find((e) => e.effect_type === 'block_area' && e.area_id === areaId && e.target_team === me);
    hornet = { sourceName: src?.source_team_name ?? null, endsAt: area.blocked_until };
  }

  let honey: AreaStatus['honey'] = null;
  for (const e of effects) {
    if (e.effect_type !== 'multiplier_area' || e.area_id !== areaId) continue;
    const m = e.multiplier ?? 1;
    if (!honey || m > honey.multiplier) honey = { multiplier: m, endsAt: e.ends_at };
  }

  let duel: AreaStatus['duel'] = null;
  for (const e of effects) {
    if (e.effect_type !== 'duel' || !(e.source_team === me || e.target_team === me)) continue;
    const iAmSource = e.source_team === me;
    const c = e.duel;
    duel = {
      mine: c ? (iAmSource ? c.source_count : c.target_count) : 0,
      theirs: c ? (iAmSource ? c.target_count : c.source_count) : 0,
      opponentName: (iAmSource ? e.target_team_name : e.source_team_name) ?? '',
      endsAt: e.ends_at,
    };
    break;
  }
  return { hornet, honey, duel };
}

/** 小札の文字（章見出しの横）。順番は 襲来・蜜源・縄張り争い */
export function chipLabels(s: AreaStatus): { kind: 'hornet' | 'honey' | 'duel'; text: string }[] {
  const out: { kind: 'hornet' | 'honey' | 'duel'; text: string }[] = [];
  if (s.hornet) out.push({ kind: 'hornet', text: '襲来' });
  if (s.honey) out.push({ kind: 'honey', text: `蜜源×${s.honey.multiplier}` });
  if (s.duel) out.push({ kind: 'duel', text: '縄張り争い' });
  return out;
}

// ---------------------------------------------------------------- 詳細のボタン（㉚）

export type SubmitButtonState =
  | { enabled: true; label: '写真を提出する' }
  | { enabled: false; label: 'クリア済み' | 'スズメバチの襲来中のためクリア不可' };

/**
 * クリア済みでも縄張り争いの最中は再提出できる（015）ので、ボタンは「写真を提出する」のまま。
 * 襲来中は出せない。クリア済みで縄張り争いも無いときだけ「クリア済み」
 */
export function submitButtonState(done: boolean, status: AreaStatus): SubmitButtonState {
  if (done && !status.duel) return { enabled: false, label: 'クリア済み' };
  if (status.hornet) return { enabled: false, label: 'スズメバチの襲来中のためクリア不可' };
  return { enabled: true, label: '写真を提出する' };
}

/** 印を出すか。縄張り争いの最中は全エリアで一時的に外す（㉘） */
export function sealVisible(done: boolean, status: AreaStatus): boolean {
  return done && !status.duel;
}

/** 目次の頭の数（クリア数／総数） */
export function clearedCount(spots: readonly StateSpot[]): { cleared: number; total: number } {
  return { cleared: spots.filter((s) => s.submitted).length, total: spots.length };
}

/** 印の傾き。スポットごとに決まった値（描き直しても動かない） */
export function sealAngle(spotId: number): number {
  const v = [-6, 5, -3, 4, -5, 3, -4, 6];
  return v[Math.abs(spotId) % v.length] ?? 0;
}
